import type {
  ApiKey,
  ApiKeyCreateBody,
  ApiKeyCreated,
  Invitation,
  InviteBody,
  Me,
  Member,
  Onboarding,
  Role,
  SignupBody,
  ActivityQuery,
  AgentEconomics,
  AnalyticsOverview,
  AnalyticsRefresh,
  AnalyticsScopeQuery,
  AskResult,
  Connector,
  ConnectorCreateBody,
  ConnectorCreated,
  ConnectorSecretBody,
  ConversationPaths,
  EnrichmentSettings,
  EnrichmentSettingsBody,
  Funnel,
  FunnelCreateBody,
  FunnelResult,
  FunnelSuggestion,
  Insight,
  InsightUpdateBody,
  OutcomesView,
  OutcomeDefinition,
  OutcomeDefinitionDecisionBody,
  PainPoint,
  PainPointCreateBody,
  PainPointUpdateBody,
  ReviewItem,
  ReviewLabelBody,
  SessionDetail,
  SessionMap,
  SimilarSessions,
  SessionQuery,
  SessionStats,
  SessionSummary,
  SignalActionBody,
  SignalBacktest,
  SignalDefinition,
  SignalDefinitionQuery,
  SignalDraftBody,
  UseCase,
  UseCaseDetail,
  UseCaseUpdateBody,
  VersionComparison,
  ActivityRecord,
  ActivityStats,
  AgentDetail,
  AgentSummary,
  AgentVersion,
  Claim,
  ClaimDecisionBody,
  ClaimEvidencePack,
  ClaimPaymentBody,
  ClaimReopenBody,
  UnderwriterOverride,
  UnderwriterOverrideBody,
  ClaimCreateBody,
  CoveragePolicy,
  Endorsement,
  EvidenceAnchor,
  EvidenceRecord,
  IssueDetail,
  IssueSummary,
  IssueTriageBody,
  Org,
  Overview,
  PackInfo,
  Page,
  Portfolio,
  PlatformProfile,
  ProtectionView,
  Quote,
  QuoteRequestBody,
  ScenarioCatalogItem,
  RiskProfile,
  Scenario,
  Settings,
  NotificationChannel,
  NotificationChannelCreateBody,
  UsageView,
  Signal,
  SignalHeatCell,
  SignalStat,
  ToolConfirmBody,
  TraceDetail,
  TraceSummary,
} from "@ti/core";
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

type Params = Record<string, string | number | boolean | undefined | null | string[]>;

function qs(params?: Params) {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, Array.isArray(v) ? v.join(",") : String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/** A signed-out request (no or expired session): go to /login and come back here afterwards. */
function redirectToLogin() {
  if (typeof window === "undefined" || window.location.pathname.startsWith("/login")) return;
  const next = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/login?next=${encodeURIComponent(next)}`);
}

async function request<T>(method: string, path: string, opts: { params?: Params; body?: unknown; signal?: AbortSignal; base?: string } = {}): Promise<T> {
  const res = await fetch(`${opts.base ?? "/api/v1"}${path}${qs(opts.params)}`, {
    method,
    headers: opts.body !== undefined ? { "content-type": "application/json" } : undefined,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: "same-origin",
  });
  if (res.status === 401 && !path.startsWith("/auth/")) redirectToLogin();
  if (!res.ok) {
    const problem = (await res.json().catch(() => null)) as { title?: string; code?: string; requestId?: string } | null;
    throw new ApiError(res.status, problem?.code ?? "http_error", problem?.title ?? `Request failed (${res.status})`, problem?.requestId);
  }
  return (await res.json()) as T;
}

const get = <T>(path: string, params?: Params) => (ctx: { signal: AbortSignal }) => request<T>("GET", path, { params, signal: ctx.signal });
/** Unauthenticated public endpoints (end-user protection page, agent-platform profiles). */
const getPublic = <T>(path: string) => (ctx: { signal: AbortSignal }) => request<T>("GET", path, { signal: ctx.signal, base: "/api/public" });

// ─── Query options (use with useQuery / useSuspenseQuery / loaders) ──────────

// ─── Sign-in (AUTH_MODE=session; the routes answer 404 in dev mode) ───────────

export interface AuthProviders {
  email: boolean;
  google: boolean;
}

/** Which sign-in methods are on; null when sign-in isn't enabled (local dev identity). */
export const authProvidersQuery = () =>
  queryOptions({
    queryKey: ["auth-providers"],
    queryFn: ({ signal }) => request<AuthProviders>("GET", "/auth/providers", { signal }).catch(() => null),
    staleTime: Infinity,
    retry: false,
  });

/** Email a sign-in link. `devLink` comes back on localhost when no email service is bound. */
export const requestLoginLink = (email: string, next?: string) => request<{ ok: true; devLink?: string }>("POST", "/auth/email", { body: { email, next } });
export const verifyLoginLink = (token: string) => request<{ ok: true; next: string }>("POST", "/auth/email/verify", { body: { token } });

export async function signOut(): Promise<void> {
  await request("POST", "/auth/logout").catch(() => undefined);
  window.location.assign("/login");
}

export const q = {
  /** Who is signed in, their orgs and pending invitations (works before an org exists). */
  me: () => queryOptions({ queryKey: ["me"], queryFn: get<Me>("/me"), staleTime: 60_000 }),
  members: () => queryOptions({ queryKey: ["members"], queryFn: get<Member[]>("/members") }),
  invitations: () => queryOptions({ queryKey: ["invitations"], queryFn: get<Invitation[]>("/invitations") }),
  apiKeys: () => queryOptions({ queryKey: ["api-keys"], queryFn: get<ApiKey[]>("/api-keys") }),
  onboarding: () => queryOptions({ queryKey: ["onboarding"], queryFn: get<Onboarding>("/onboarding"), staleTime: 60_000 }),
  org: () => queryOptions({ queryKey: ["org"], queryFn: get<Org>("/org"), staleTime: 5 * 60_000 }),
  overview: () => queryOptions({ queryKey: ["overview"], queryFn: get<Overview>("/overview") }),

  agents: () => queryOptions({ queryKey: ["agents"], queryFn: get<AgentSummary[]>("/agents") }),
  agent: (id: string) => queryOptions({ queryKey: ["agents", id], queryFn: get<AgentDetail>(`/agents/${id}`) }),
  agentVersions: (id: string) => queryOptions({ queryKey: ["agents", id, "versions"], queryFn: get<AgentVersion[]>(`/agents/${id}/versions`) }),
  riskProfile: (id: string) => queryOptions({ queryKey: ["agents", id, "profile"], queryFn: get<RiskProfile>(`/agents/${id}/profile`) }),
  agentScenarios: (id: string) => queryOptions({ queryKey: ["agents", id, "scenarios"], queryFn: get<Scenario[]>(`/agents/${id}/scenarios`) }),

  traces: (params: { q?: string; agentId?: string; outcome?: string; severity?: string; signal?: string; from?: string; to?: string; limit?: number; cursor?: string }) =>
    queryOptions({ queryKey: ["traces", params], queryFn: get<Page<TraceSummary>>("/traces", params), placeholderData: keepPreviousData }),
  trace: (traceId: string) => queryOptions({ queryKey: ["traces", "detail", traceId], queryFn: get<TraceDetail>(`/traces/${traceId}`), staleTime: Infinity }),

  signals: (params: { agentId?: string; type?: string; limit?: number }) => queryOptions({ queryKey: ["signals", params], queryFn: get<Signal[]>("/signals", params) }),
  signalStats: () => queryOptions({ queryKey: ["signals", "stats"], queryFn: get<SignalStat[]>("/signals/stats") }),
  signalHeatmap: () => queryOptions({ queryKey: ["signals", "heatmap"], queryFn: get<SignalHeatCell[]>("/signals/heatmap") }),

  issues: (params: { agentId?: string; state?: string[]; severity?: string[]; limit?: number } = {}) =>
    queryOptions({ queryKey: ["issues", params], queryFn: get<Page<IssueSummary>>("/issues", { limit: 200, ...params }) }),
  issue: (id: string) => queryOptions({ queryKey: ["issues", "detail", id], queryFn: get<IssueDetail>(`/issues/${id}`) }),

  activity: (params: Partial<ActivityQuery>) =>
    queryOptions({ queryKey: ["activity", params], queryFn: get<Page<ActivityRecord>>("/activity", params), placeholderData: keepPreviousData }),
  activityStats: (params: { agentId?: string } = {}) =>
    queryOptions({ queryKey: ["activity", "stats", params], queryFn: get<ActivityStats>("/activity/stats", params) }),
  packs: () => queryOptions({ queryKey: ["packs"], queryFn: get<PackInfo[]>("/packs"), staleTime: 5 * 60_000 }),

  policies: () => queryOptions({ queryKey: ["coverage", "policies"], queryFn: get<CoveragePolicy[]>("/policies") }),
  policy: (id: string) => queryOptions({ queryKey: ["coverage", "policies", id], queryFn: get<CoveragePolicy>(`/policies/${id}`) }),
  scenarios: (params: { policyId?: string; agentId?: string; state?: string } = {}) => queryOptions({ queryKey: ["coverage", "scenarios", params], queryFn: get<Scenario[]>("/coverage/scenarios", params) }),
  endorsements: (params: { policyId?: string } = {}) => queryOptions({ queryKey: ["coverage", "endorsements", params], queryFn: get<Endorsement[]>("/coverage/endorsements", params) }),
  scenarioCatalog: (agentIds: string[] = []) => queryOptions({ queryKey: ["coverage", "catalog", agentIds], queryFn: get<ScenarioCatalogItem[]>("/coverage/catalog", { agentIds }) }),
  claims: (params: { policyId?: string } = {}) => queryOptions({ queryKey: ["claims", params], queryFn: get<Claim[]>("/claims", params) }),
  claim: (id: string) => queryOptions({ queryKey: ["claims", id], queryFn: get<Claim>(`/claims/${id}`) }),

  evidence: (limit = 100) => queryOptions({ queryKey: ["evidence", limit], queryFn: get<EvidenceRecord[]>("/evidence", { limit }) }),
  anchors: () => queryOptions({ queryKey: ["evidence", "anchors"], queryFn: get<EvidenceAnchor[]>("/evidence/anchors") }),

  // Product analytics (sessions, use cases, funnels, pain points, dynamic signals, insights)
  analyticsOverview: () => queryOptions({ queryKey: ["analytics", "overview"], queryFn: get<AnalyticsOverview>("/analytics/overview") }),
  sessions: (params: Partial<SessionQuery>) =>
    queryOptions({ queryKey: ["sessions", params], queryFn: get<Page<SessionSummary>>("/sessions", params), placeholderData: keepPreviousData }),
  session: (id: string) => queryOptions({ queryKey: ["sessions", "detail", id], queryFn: get<SessionDetail>(`/sessions/${id}`) }),
  similarSessions: (id: string, sameAgent: boolean) =>
    queryOptions({ queryKey: ["sessions", "similar", id, sameAgent], queryFn: get<SimilarSessions>(`/sessions/${id}/similar`, { sameAgent, limit: 8 }), staleTime: 5 * 60_000 }),
  /** Aggregates over the same filters as `sessions` (paging params are ignored). */
  sessionStats: (params: Partial<SessionQuery>) =>
    queryOptions({ queryKey: ["sessions", "stats", params], queryFn: get<SessionStats>("/sessions/stats", params), placeholderData: keepPreviousData }),
  useCases: (params: { agentId?: string } = {}) => queryOptions({ queryKey: ["use-cases", params], queryFn: get<UseCase[]>("/use-cases", params) }),
  sessionMap: (params: { agentId?: string } = {}) => queryOptions({ queryKey: ["use-cases", "map", params], queryFn: get<SessionMap>("/use-cases/map", params), staleTime: 5 * 60_000 }),
  outcomeDefinitions: (params: { useCaseId?: string; status?: "proposed" | "active" | "dismissed" } = {}) =>
    queryOptions({ queryKey: ["outcome-definitions", params], queryFn: get<OutcomeDefinition[]>("/outcome-definitions", params) }),
  useCase: (id: string) => queryOptions({ queryKey: ["use-cases", "detail", id], queryFn: get<UseCaseDetail>(`/use-cases/${id}`) }),
  paths: (params: AnalyticsScopeQuery = {}) => queryOptions({ queryKey: ["analytics", "paths", params], queryFn: get<ConversationPaths>("/analytics/paths", params) }),
  outcomes: (params: AnalyticsScopeQuery = {}) => queryOptions({ queryKey: ["analytics", "outcomes", params], queryFn: get<OutcomesView>("/analytics/outcomes", params) }),
  funnels: () => queryOptions({ queryKey: ["funnels"], queryFn: get<Funnel[]>("/funnels") }),
  funnel: (id: string) => queryOptions({ queryKey: ["funnels", id], queryFn: get<FunnelResult>(`/funnels/${id}`) }),
  funnelSuggestions: () => queryOptions({ queryKey: ["funnel-suggestions"], queryFn: get<FunnelSuggestion[]>("/funnels/suggestions") }),
  painPoints: (params: { agentId?: string; kind?: string; status?: string } = {}) =>
    queryOptions({ queryKey: ["pain-points", params], queryFn: get<PainPoint[]>("/pain-points", params) }),
  signalDefinitions: (params: SignalDefinitionQuery = {}) =>
    queryOptions({ queryKey: ["signal-definitions", params], queryFn: get<SignalDefinition[]>("/signal-definitions", params) }),
  signalDefinition: (id: string) => queryOptions({ queryKey: ["signal-definitions", "detail", id], queryFn: get<SignalDefinition>(`/signal-definitions/${id}`) }),
  reviewQueue: (params: { definitionId?: string; labeled?: boolean } = {}) =>
    queryOptions({ queryKey: ["review-queue", params], queryFn: get<ReviewItem[]>("/review-queue", params) }),
  insights: () => queryOptions({ queryKey: ["insights"], queryFn: get<Insight[]>("/insights") }),
  economics: () => queryOptions({ queryKey: ["analytics", "economics"], queryFn: get<AgentEconomics[]>("/economics") }),
  versionCompare: (agentId: string, a: string, b: string) =>
    queryOptions({ queryKey: ["agents", agentId, "versions", "compare", a, b], queryFn: get<VersionComparison>(`/agents/${agentId}/versions/compare`, { a, b }) }),

  portfolio: () => queryOptions({ queryKey: ["underwriter", "portfolio"], queryFn: get<Portfolio>("/underwriter/portfolio") }),
  underwriterOverrides: () => queryOptions({ queryKey: ["underwriter", "overrides"], queryFn: get<UnderwriterOverride[]>("/underwriter/overrides") }),
  settings: () => queryOptions({ queryKey: ["settings"], queryFn: get<Settings>("/settings") }),
  notificationChannels: () => queryOptions({ queryKey: ["notifications", "channels"], queryFn: get<NotificationChannel[]>("/notifications/channels") }),
  usage: () => queryOptions({ queryKey: ["usage"], queryFn: get<UsageView>("/usage"), staleTime: 5 * 60_000 }),
  connectors: () => queryOptions({ queryKey: ["connectors"], queryFn: get<Connector[]>("/connectors") }),
  protection: (orgSlug: string) => queryOptions({ queryKey: ["public", "protect", orgSlug], queryFn: getPublic<ProtectionView>(`/protect/${orgSlug}`) }),
  platformProfile: (slug: string) => queryOptions({ queryKey: ["public", "platform", slug], queryFn: getPublic<PlatformProfile>(`/platforms/${slug}`), staleTime: 5 * 60_000 }),
};

// ─── Mutations ───────────────────────────────────────────────────────────────

function onErr(e: Error) {
  toast.error(e.message);
}

export function useIssueAction(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IssueTriageBody) => request<IssueDetail>("POST", `/issues/${id}/actions`, { body }),
    onSuccess: (data) => {
      qc.setQueryData(q.issue(id).queryKey, data);
      void qc.invalidateQueries({ queryKey: ["issues"] });
      void qc.invalidateQueries({ queryKey: ["overview"] });
      void qc.invalidateQueries({ queryKey: ["coverage"] });
      void qc.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: onErr,
  });
}

export function useConfirmTool(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ToolConfirmBody) => request<RiskProfile>("POST", `/agents/${agentId}/profile/tools`, { body }),
    onSuccess: (data) => {
      qc.setQueryData(q.riskProfile(agentId).queryKey, data);
      void qc.invalidateQueries({ queryKey: ["coverage"] });
      toast.success("Tool classification confirmed");
    },
    onError: onErr,
  });
}

export function useCreateQuote() {
  return useMutation({
    mutationFn: (body: QuoteRequestBody) => request<Quote>("POST", "/coverage/quotes", { body }),
    onError: onErr,
  });
}

export function useBindQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quoteId: string) => request<CoveragePolicy>("POST", "/policies", { body: { quoteId } }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ["coverage"] });
      void qc.invalidateQueries({ queryKey: ["agents"] });
      void qc.invalidateQueries({ queryKey: ["overview"] });
      toast.success(`${p.name} submitted for binding`);
    },
    onError: onErr,
  });
}

export function useCreateClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClaimCreateBody) => request<Claim>("POST", "/claims", { body }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["claims"] });
      toast.success(`Claim ${c.id} filed`);
    },
    onError: onErr,
  });
}

/** Program claims: an underwriter's decision, the payment, or reopening a denial. */
export function useClaimAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { id: string } & ({ kind: "decision"; body: ClaimDecisionBody } | { kind: "payment"; body: ClaimPaymentBody } | { kind: "reopen"; body: ClaimReopenBody })) =>
      request<Claim>("POST", `/claims/${a.id}/${a.kind}`, { body: a.body }),
    onSuccess: (c) => {
      qc.setQueryData(q.claim(c.id).queryKey, c);
      void qc.invalidateQueries({ queryKey: ["claims"] });
      void qc.invalidateQueries({ queryKey: ["coverage"] });
      void qc.invalidateQueries({ queryKey: ["evidence"] });
      toast.success(`${c.id} ${c.status === "paid" ? "marked paid" : c.status === "under_review" ? "reopened" : c.status}`);
    },
    onError: onErr,
  });
}

/** Download the claim's sealed evidence pack as JSON. */
export async function downloadEvidencePack(id: string): Promise<void> {
  const pack = await request<ClaimEvidencePack>("GET", `/claims/${id}/evidence-pack`);
  const { downloadText } = await import("./download");
  downloadText(`${id}-evidence.json`, JSON.stringify(pack, null, 2));
}

/** End user files a claim on the customer's public protection page (no login). */
export function usePublicClaim(orgSlug: string) {
  return useMutation({
    mutationFn: (body: Omit<ClaimCreateBody, "policyId">) => request<Claim>("POST", `/protect/${orgSlug}/claims`, { body, base: "/api/public" }),
    onError: onErr,
  });
}

export function useRenameOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => request<Org>("PATCH", "/org", { body: { name } }),
    onSuccess: (data) => {
      qc.setQueryData(q.org().queryKey, data);
      void qc.invalidateQueries({ queryKey: q.me().queryKey });
      toast.success("Organization renamed");
    },
    onError: onErr,
  });
}

export function useUnderwriterOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UnderwriterOverrideBody) => request<UnderwriterOverride>("POST", "/underwriter/overrides", { body }),
    onSuccess: (o) => {
      void qc.invalidateQueries({ queryKey: ["underwriter"] });
      void qc.invalidateQueries({ queryKey: ["coverage"] });
      void qc.invalidateQueries({ queryKey: ["issues"] });
      toast.success("Override recorded", { description: `${o.agentName}: ${o.detail}` });
    },
    onError: onErr,
  });
}

/** Accept or dismiss a proposed outcome definition. */
export function useDecideOutcomeDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { id: string; body: OutcomeDefinitionDecisionBody }) => request<OutcomeDefinition>("POST", `/outcome-definitions/${a.id}/decision`, { body: a.body }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ["outcome-definitions"] });
      void qc.invalidateQueries({ queryKey: ["analytics"] });
      toast.success(d.status === "active" ? `“${d.name}” now decides outcomes` : "Suggestion dismissed", d.status === "active" ? { description: "It applies to sessions enriched from now on." } : undefined);
    },
    onError: onErr,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Pick<Settings, "privacyMode" | "retentionDays">>) => request<Settings>("PATCH", "/settings", { body: patch }),
    onSuccess: (data) => {
      qc.setQueryData(q.settings().queryKey, data);
      toast.success("Settings saved");
    },
    onError: onErr,
  });
}

// ─── Product analytics mutations ─────────────────────────────────────────────

export function useUpdateUseCase(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UseCaseUpdateBody) => request<UseCase>("POST", `/use-cases/${id}/actions`, { body }),
    onSuccess: (data, vars) => {
      void qc.invalidateQueries({ queryKey: ["use-cases"] });
      void qc.invalidateQueries({ queryKey: ["analytics"] });
      void qc.invalidateQueries({ queryKey: ["sessions"] });
      // A merge returns the use case that absorbed this one.
      toast.success(vars.action === "merge" ? `Merged into ${data.name}` : vars.action === "hide" ? `${data.name} hidden` : `${data.name} ${vars.action === "rename" ? "renamed" : "confirmed"}`);
    },
    onError: onErr,
  });
}

export function useCreateFunnel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FunnelCreateBody) => request<Funnel>("POST", "/funnels", { body }),
    onSuccess: (f) => {
      void qc.invalidateQueries({ queryKey: ["funnels"] });
      void qc.invalidateQueries({ queryKey: ["funnel-suggestions"] });
      void qc.invalidateQueries({ queryKey: ["analytics", "overview"] });
      toast.success(`${f.name} created — backfilling the last 30 days`);
    },
    onError: onErr,
  });
}

export function useDismissFunnelSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<{ ok: true }>("POST", `/funnels/suggestions/${id}/dismiss`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["funnel-suggestions"] });
      toast.success("Suggestion dismissed");
    },
    onError: onErr,
  });
}

export function useDeleteFunnel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<{ ok: true }>("DELETE", `/funnels/${id}`),
    onSuccess: (_d, id) => {
      qc.removeQueries({ queryKey: q.funnel(id).queryKey });
      void qc.invalidateQueries({ queryKey: ["funnels"] });
      void qc.invalidateQueries({ queryKey: ["analytics", "overview"] });
      toast.success("Funnel deleted");
    },
    onError: onErr,
  });
}

export function useUpdatePainPoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: PainPointUpdateBody & { id: string }) => request<PainPoint>("PATCH", `/pain-points/${id}`, { body }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ["pain-points"] });
      void qc.invalidateQueries({ queryKey: ["analytics", "overview"] });
      toast.success(`Pain point marked ${p.status}`);
    },
    onError: onErr,
  });
}

export function useCreatePainPoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<PainPointCreateBody> & Pick<PainPointCreateBody, "kind" | "title" | "agentIds">) => request<PainPoint>("POST", "/pain-points", { body }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ["pain-points"] });
      void qc.invalidateQueries({ queryKey: ["analytics", "overview"] });
      toast.success(`Pain point created: ${p.title}`);
    },
    onError: onErr,
  });
}

export function useDraftSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<SignalDraftBody> & Pick<SignalDraftBody, "name" | "description">) => request<SignalDefinition>("POST", "/signal-definitions", { body }),
    onSuccess: (d) => {
      qc.setQueryData(q.signalDefinition(d.id).queryKey, d);
      void qc.invalidateQueries({ queryKey: ["signal-definitions"] });
      void qc.invalidateQueries({ queryKey: ["pain-points"] });
      toast.success(`${d.name} drafted — backtest ready`);
    },
    onError: onErr,
  });
}

export function useSignalAction(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SignalActionBody) => request<SignalDefinition>("POST", `/signal-definitions/${id}/actions`, { body }),
    onSuccess: (d, vars) => {
      qc.setQueryData(q.signalDefinition(id).queryKey, d);
      void qc.invalidateQueries({ queryKey: ["signal-definitions"] });
      void qc.invalidateQueries({ queryKey: ["review-queue"] });
      void qc.invalidateQueries({ queryKey: ["analytics", "overview"] });
      toast.success(vars.action === "edit" ? `${d.name} updated (v${d.version})` : vars.action === "nominate" ? `${d.name} nominated for certification` : `${d.name} → ${d.status}`);
    },
    onError: onErr,
  });
}

export function useBacktestSignal(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => request<SignalBacktest>("POST", `/signal-definitions/${id}/backtest`),
    onSuccess: (b) => {
      void qc.invalidateQueries({ queryKey: ["signal-definitions"] });
      toast.success(`Backtest done: ${b.hits.toLocaleString()} hits in ${b.sessionsScanned.toLocaleString()} sessions`);
    },
    onError: onErr,
  });
}

export function useLabelReviewItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ReviewLabelBody & { id: string }) => request<ReviewItem>("POST", `/review-queue/${id}/label`, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["review-queue"] });
      void qc.invalidateQueries({ queryKey: ["signal-definitions"] });
    },
    onError: onErr,
  });
}

export function useUpdateInsight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: InsightUpdateBody & { id: string }) => request<Insight>("PATCH", `/insights/${id}`, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["insights"] });
      void qc.invalidateQueries({ queryKey: ["analytics", "overview"] });
    },
    onError: onErr,
  });
}

export function useAsk() {
  return useMutation({
    mutationFn: (question: string) => request<AskResult>("POST", "/ask", { body: { question } }),
    onError: onErr,
  });
}

export function useUpdateEnrichmentSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: EnrichmentSettingsBody) => request<EnrichmentSettings>("PATCH", "/settings/enrichment", { body: patch }),
    onSuccess: (data) => {
      qc.setQueryData(q.settings().queryKey, (s) => (s ? { ...s, enrichment: data } : s));
      toast.success("Enrichment settings saved");
    },
    onError: onErr,
  });
}

// ─── Business-event connectors ───────────────────────────────────────────────

/** Returns the signing secret once (show it, then drop it from memory). */
export function useCreateConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConnectorCreateBody) => request<ConnectorCreated>("POST", "/connectors", { body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["connectors"] }),
    onError: onErr,
  });
}

export function useSetConnectorSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ConnectorSecretBody & { id: string }) => request<Connector>("PUT", `/connectors/${id}/secret`, { body }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["connectors"] });
      toast.success(`Signing secret saved for ${c.name}`);
    },
    onError: onErr,
  });
}

export function useDeleteConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<{ ok: true }>("DELETE", `/connectors/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["connectors"] });
      toast.success("Connector deleted", { description: "Its webhook URL stops accepting events within a minute." });
    },
    onError: onErr,
  });
}

// ─── Notification channels ───────────────────────────────────────────────────

/** A webhook channel's response carries `signingSecret` once (show it, then drop it). */
export function useCreateNotificationChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationChannelCreateBody) => request<NotificationChannel>("POST", "/notifications/channels", { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
      void qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: onErr,
  });
}

export function useDeleteNotificationChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<{ ok: true }>("DELETE", `/notifications/channels/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
      void qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Notification channel deleted");
    },
    onError: onErr,
  });
}

export function useTestNotificationChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<{ ok: boolean; status: number | null; error: string | null }>("POST", `/notifications/channels/${encodeURIComponent(id)}/test`),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["notifications", "channels"] });
      if (r.ok) toast.success("Test notification sent");
      else toast.error("Test notification failed", { description: r.error ?? (r.status ? `HTTP ${r.status}` : undefined) });
    },
    onError: onErr,
  });
}

// ─── Accounts: org selection, signup, invitations, members, API keys ─────────

/** The org cookie the API reads when a person belongs to several orgs (worker/auth.ts). */
export const ORG_COOKIE = "ti_org";

/** Select the org for every following request, then refetch everything for it. */
export function selectOrg(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  document.cookie = `${ORG_COOKIE}=${encodeURIComponent(orgId)}; Path=/; Max-Age=${365 * 86_400}; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
  qc.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
  void qc.invalidateQueries({ queryKey: ["me"] });
}

/** Absolute link a teammate opens to accept an invitation (they sign in with the invited email). */
export const invitationLink = (id: string) => `${location.origin}/join/${encodeURIComponent(id)}`;

/** Signup, accept and decline answer with the updated Me and select the resulting org (cookie). */
function useMeMutation<V>(fn: (v: V) => Promise<Me>, success?: (me: Me) => string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (me) => {
      qc.setQueryData(q.me().queryKey, me);
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
      const msg = success?.(me);
      if (msg) toast.success(msg);
    },
    onError: onErr,
  });
}

export function useSignup() {
  return useMeMutation((body: Partial<SignupBody> & { orgName: string }) => request<Me>("POST", "/signup", { body }), (me) => `Welcome to ${me.orgs.find((o) => o.id === me.currentOrgId)?.name ?? "your new org"}`);
}

export function useAcceptInvitation() {
  return useMeMutation((id: string) => request<Me>("POST", `/invitations/${encodeURIComponent(id)}/accept`), (me) => `You joined ${me.orgs.find((o) => o.id === me.currentOrgId)?.name ?? "the org"}`);
}

export function useDeclineInvitation() {
  return useMeMutation((id: string) => request<Me>("POST", `/invitations/${encodeURIComponent(id)}/decline`), () => "Invitation declined");
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteBody) => request<Invitation>("POST", "/invitations", { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invitations"] });
      void qc.invalidateQueries({ queryKey: ["onboarding"] });
    },
    onError: onErr,
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<{ ok: true }>("DELETE", `/invitations/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invitations"] });
      toast.success("Invitation revoked");
    },
    onError: onErr,
  });
}

export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) => request<Member>("PATCH", `/members/${encodeURIComponent(userId)}`, { body: { role } }),
    onSuccess: (m) => {
      void qc.invalidateQueries({ queryKey: ["members"] });
      void qc.invalidateQueries({ queryKey: ["settings"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
      toast.success(`${m.name}'s role is now ${m.role}`);
    },
    onError: onErr,
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => request<{ ok: true }>("DELETE", `/members/${encodeURIComponent(userId)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["members"] });
      void qc.invalidateQueries({ queryKey: ["settings"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: onErr,
  });
}

/** Owners and admins: rebuild today's rollups now and start discovery (use cases, pain points, insights). */
export function useRefreshAnalytics() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => request<AnalyticsRefresh>("POST", "/analytics/refresh"),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["analytics"] });
      void qc.invalidateQueries({ queryKey: ["sessions"] });
      toast.success(r.discoveryStarted ? "Analytics refreshed. Use cases, pain points and insights update in a few minutes." : "Analytics refreshed.");
      // Discovery finishes in the background: pick up its results without a manual reload.
      if (r.discoveryStarted) setTimeout(() => void qc.invalidateQueries(), 4 * 60_000);
    },
    onError: onErr,
  });
}

/** Returns the key secret once (show it, then drop it from memory). */
export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApiKeyCreateBody) => request<ApiKeyCreated>("POST", "/api-keys", { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api-keys"] });
      void qc.invalidateQueries({ queryKey: ["settings"] });
      void qc.invalidateQueries({ queryKey: ["onboarding"] });
    },
    onError: onErr,
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: Pick<ApiKey, "id" | "name">) => request<{ ok: true }>("DELETE", `/api-keys/${encodeURIComponent(key.id)}`),
    onSuccess: (_r, key) => {
      void qc.invalidateQueries({ queryKey: ["api-keys"] });
      void qc.invalidateQueries({ queryKey: ["settings"] });
      void qc.invalidateQueries({ queryKey: ["onboarding"] });
      toast.success(`Key “${key.name}” revoked`, { description: "Requests using it are rejected within 60 seconds." });
    },
    onError: onErr,
  });
}
